/* eslint-disable camelcase */
import { ADMITTED_STATUS, CANCELLED_STATUS, ORDER_STATUS_ID_REQUESTED, PREPARED_STATUS, PROCESSED_STATUS } from './constants'

export type WarehousePreparationOrder = {
  status_id?: number | null
  cancelation_date?: string | null
  tracking_id?: string | null
  shipments?: Array<{ tracking_id?: string | null, processed_at?: string | null }> | null
  created_at?: string | null
  shipping_method?: { name?: string | null } | null
  warehouseNote?: string | null
  supplements?: Array<{
    volumetric?: boolean | null
    deleted?: boolean | null
    status_id?: number | { id?: number | null } | null
  }> | null
  order?: WarehousePreparationOrder | null
}

export type WarehouseVolumetricTarget = {
  volumetric?: boolean | null
  status_id?: number | { id?: number | null } | null
  created_at?: string | null
}

export type WarehousePreparationBlock =
  | 'CANCELLED'
  | 'CANCELLATION_REQUESTED'
  | 'ALREADY_PREPARED'
  | 'DUPLICATE_VOLUMETRIC_PENDING'
  | 'DUPLICATE_VOLUMETRIC_ADMITTED'
  | 'NOT_PREPARABLE_STATUS'
  | null

const orderFrom = (value?: WarehousePreparationOrder | null): WarehousePreparationOrder | undefined => value?.order || value || undefined

const supplementStatusId = (supplement?: { status_id?: number | { id?: number | null } | null }): number | undefined => {
  const status = supplement?.status_id
  return typeof status === 'object' ? Number(status?.id) : Number(status)
}

// A paid/admitted volumetric supplement is immutable in Warehouse. Pending
// payment remains editable so the team can correct the package data.
export const isWarehouseVolumetricEditable = (supplement?: {
  volumetric?: boolean | null
  status_id?: number | { id?: number | null } | null
}): boolean => !supplement?.volumetric || supplementStatusId(supplement) !== ADMITTED_STATUS

// A second admitted volumetric supplement is a deliberately new dispatch. It
// must remain preparable even when the order already has an older label. The
// relation is chronological: the new supplement has been created after the
// latest shipment that already has tracking.
const isNewAdmittedVolumetricDispatch = (order: WarehousePreparationOrder | undefined, target?: WarehouseVolumetricTarget | null): boolean => {
  if (!order || !target?.volumetric || supplementStatusId(target) !== ADMITTED_STATUS || !target.created_at) return false
  const preparedAt = (order.shipments || [])
    .filter((shipment) => Boolean(shipment.tracking_id) && Boolean(shipment.processed_at))
    .map((shipment) => Date.parse(shipment.processed_at!))
    .filter(Number.isFinite)

  if (preparedAt.length === 0) return false
  const targetCreatedAt = Date.parse(target.created_at)
  return Number.isFinite(targetCreatedAt) && targetCreatedAt > Math.max(...preparedAt)
}

// Warehouse may not override the commercial lifecycle. This guard is reused by
// every warehouse entry point and again immediately before the carrier call.
export const getWarehousePreparationBlock = (value?: WarehousePreparationOrder | null, targetVolumetric?: WarehouseVolumetricTarget | null): WarehousePreparationBlock => {
  const order = orderFrom(value)
  if (!order) return 'NOT_PREPARABLE_STATUS'
  if (order.status_id === CANCELLED_STATUS) return 'CANCELLED'
  if (order.cancelation_date) return 'CANCELLATION_REQUESTED'
  const newVolumetricDispatch = isNewAdmittedVolumetricDispatch(order, targetVolumetric)
  if ((order.tracking_id || order.shipments?.some((shipment) => Boolean(shipment.tracking_id))) && !newVolumetricDispatch) return 'ALREADY_PREPARED'
  if (![PROCESSED_STATUS, ADMITTED_STATUS].includes(Number(order.status_id)) && !(order.status_id === PREPARED_STATUS && newVolumetricDispatch)) return 'NOT_PREPARABLE_STATUS'
  return null
}

export const getWarehouseVolumetricBlock = (value?: WarehousePreparationOrder | null): WarehousePreparationBlock => {
  const lifecycleBlock = getWarehousePreparationBlock(value)
  if (lifecycleBlock) return lifecycleBlock
  const order = orderFrom(value)
  const existingVolumetric = order?.supplements?.find((supplement) => supplement.volumetric && !supplement.deleted)
  if (existingVolumetric) {
    return supplementStatusId(existingVolumetric) === ADMITTED_STATUS
      ? 'DUPLICATE_VOLUMETRIC_ADMITTED'
      : 'DUPLICATE_VOLUMETRIC_PENDING'
  }
  return null
}

// A warehouse note must be shown, but it never changes the lifecycle decision.
export const hasWarehousePreparationNotice = (value?: WarehousePreparationOrder | null): boolean => Boolean(orderFrom(value)?.warehouseNote?.trim())

export type WarehousePreparationAction = 'prepare' | 'supplement'

// Warehouse has purpose-specific copy. Other profiles keep the panel's legacy
// confirmable warnings and are never routed through this mapper.
export const warehousePreparationAdviceKey = (value: WarehousePreparationOrder | null | undefined, action: WarehousePreparationAction): string => {
  const order = orderFrom(value)
  const block = action === 'supplement' ? getWarehouseVolumetricBlock(value) : getWarehousePreparationBlock(value)
  if (block === 'CANCELLED') return 'supplement.warehouseBlock.cancelled'
  if (block === 'CANCELLATION_REQUESTED') return 'supplement.warehouseBlock.cancellationRequest'
  if (block === 'ALREADY_PREPARED' || order?.status_id === PREPARED_STATUS) return 'supplement.warehouseBlock.alreadyPrepared'
  if (block === 'DUPLICATE_VOLUMETRIC_PENDING') return 'supplement.warehouseBlock.duplicateVolumetricPending'
  if (block === 'DUPLICATE_VOLUMETRIC_ADMITTED') return 'supplement.warehouseBlock.duplicateVolumetricAdmitted'
  if (order?.status_id === ORDER_STATUS_ID_REQUESTED) return 'supplement.warehouseBlock.requested'
  return 'supplement.warehouseBlock.unavailable'
}
